import { TFunction } from "i18next";

import { TableData } from "./table";
import {
    BBTIConfiguration,
    DownloadServerConfiguration,
    IdentityVerificationConfiguration,
    InsuranceConfiguration,
    LeaseConfiguration,
    NtfConfiguration,
    SdkConfiguration,
    ValidationConfiguration,
} from "components/licenses/license-configuration/bms/BmsCommonInterfaces";
import { formatTimestamp } from "utils/format";

export interface LicenseData extends TableData {
    product: string;
    license: string;
    total: number;
    available: number;
    expirationDate: string;
    type: string;
    licenseType: string;
    isInvalid?: boolean;
    isFeatureLicensePresent: boolean;
    used?: number;
    usesLeft?: number;
    subLicenses?: LicenseData[];
    overallRemaining?: number;
    assigned?: number;
    poolName?: string;
    recursiveBalance?: number;
    deliveryMode?: string;
}

export interface Key {
    licenseKey: string;
    identifier: string;
    assigned: number;
    used: number;
    remaining: number;
    startDate: string;
    expirationDate: string;
    usageHoursLimit: number;
    licenseType: string;
    shortUrl: string;
    poolName: string;
    enabled?: boolean;
    checked: boolean;
}

export interface AvailableLicenseData {
    availableLicenses: number;
    expiration?: string;
}

export interface KeyCursor {
    licenseKey: string;
    tenantIdentifier: string;
    created: string;
}

export interface LicenseKeyCursor {
    identifier: string;
    tenantIdentifier: string;
    created: string;
}

export interface Keys extends TableData {
    licenseKeys?: Key[];
    cursor?: KeyCursor | null;
    available?: boolean;
}

export interface KeyData {
    licenseKeys: Keys;
}

export interface Licenses extends TableData {
    licenseData: LicenseData[];
    cursor: LicenseKeyCursor | null;
}

export interface LicenseList {
    licenses: Licenses;
}

export interface ConfigureData extends TableData {
    uuid: string;
    product: string;
    created: string;
    modified: string;
}

export interface Entitlement {
    entitlementId: string;
    created: string;
    type: EntitlementType;
    description: string;
    backupUuid: string;
    containerId: string;
    tenantUuid: string;
    mcName: string;
    licenses: EntitlementLicenseDto[] | null;
    state: EntitlementState | null;
    updateDate?: string;
}

export interface EntitlementLicense {
    licenseId: number;
    expirationDate: string;
    amount: number;
}

export interface EntitlementLicenseDto {
    license_id: number;
    expiration_date: string;
    amount: number;
}

export function toEntitlementLicense(dto: EntitlementLicenseDto): EntitlementLicense {
    return {
        licenseId: dto.license_id,
        expirationDate: dto.expiration_date,
        amount: dto.amount,
    };
}

export interface EntitlementCursor {
    lastTenantUuid: string;
    lastEntitlementId: string;
    lastCreated: string;
}

export interface Entitlements extends TableData {
    entitlements: Entitlement[];
    cursor: EntitlementCursor | null;
    count: number;
}

export interface EntitlementData {
    entitlements: Entitlements;
}

export interface ComputedLicenses {
    licenseId: string;
    amount: number;
}

export enum EntitlementType {
    HL = "HL",
    HL_UPDATE = "HL_UPDATE",
    SL_ADMIN_MODE = "SL_AdminMode",
    SL_CREATE = "SL_CREATE",
    SL_UPDATE = "SL_UPDATE",
}

export enum ContainerType {
    HL = "HL",
    SL_ADMIN_MODE = "SL_AdminMode",
    SL_CREATE = "SL_CREATE",
    SL_UPDATE = "SL_UPDATE",
    HL_UPDATE = "HL_UPDATE",
}

export enum EntitlementState {
    WAITING_FOR_C2V = "WAITING_FOR_C2V",
    IN_PROGRESS = "IN_PROGRESS",
    ACTIVATED = "ACTIVATED",
    FAILED = "FAILED",
    MANUALLY_ACTIVATED = "MANUALLY_ACTIVATED",
}

export const getEntitlementStateDisplay = (t: TFunction, status: EntitlementState, activationDate?: string): string => {
    const entitlementActivationDate = activationDate && formatTimestamp(activationDate);
    const statusMap: Record<EntitlementState, string> = {
        [EntitlementState.ACTIVATED]: t("Entitlements.entitlementTable.state.activated", {
            activationDate: entitlementActivationDate,
        }),
        [EntitlementState.WAITING_FOR_C2V]: t("Entitlements.entitlementTable.state.pending"),
        [EntitlementState.IN_PROGRESS]: t("Entitlements.entitlementTable.state.pending"),
        [EntitlementState.FAILED]: t("Entitlements.entitlementTable.state.failed"),
        [EntitlementState.MANUALLY_ACTIVATED]: t("Entitlements.entitlementTable.state.manuallyActivated", {
            activationDate: entitlementActivationDate,
        }),
    };

    return statusMap[status] || status;
};

export const getEntitlementStateInfo = (
    t: TFunction,
    status: EntitlementState,
    entitlemntUpdationDate?: string
): string => {
    const entitlementUpdationDate = entitlemntUpdationDate && formatTimestamp(entitlemntUpdationDate);
    const statusMap: Record<EntitlementState, string> = {
        [EntitlementState.ACTIVATED]: t("Mcs.onPremBmpInfoModal.entitlementStates.activated", {
            lastUpdatedDate: entitlementUpdationDate,
        }),
        [EntitlementState.WAITING_FOR_C2V]: t("Mcs.onPremBmpInfoModal.entitlementStates.waiting", {
            lastUpdatedDate: entitlementUpdationDate,
        }),
        [EntitlementState.IN_PROGRESS]: t("Mcs.onPremBmpInfoModal.entitlementStates.inProgress", {
            lastUpdatedDate: entitlementUpdationDate,
        }),
        [EntitlementState.FAILED]: t("Mcs.onPremBmpInfoModal.entitlementStates.failed", {
            lastUpdatedDate: entitlementUpdationDate,
        }),
        [EntitlementState.MANUALLY_ACTIVATED]: t("Mcs.onPremBmpInfoModal.entitlementStates.manuallyActivated", {
            lastUpdatedDate: entitlementUpdationDate,
        }),
    };

    return statusMap[status] || status;
};
export interface Container {
    containerId: string;
    tenantUuid: string;
    date: string;
    name: string;
    type: ContainerType;
}

export interface ContainerCursor {
    lastTenantUuid: string;
    lastContainerId: string;
}

export interface Containers extends TableData {
    containers: Container[];
    cursor: ContainerCursor | null;
}

export interface ContainerData {
    containers: Containers;
}

export interface Configuration {
    tenantConfiguration: Record<string, unknown>;
    bbtiConfiguration: BBTIConfiguration;
    validationConfiguration: ValidationConfiguration;
    insuranceConfiguration: InsuranceConfiguration;
    ntfConfiguration: NtfConfiguration;
    leaseConfiguration: LeaseConfiguration;
    downloadAppPageConfiguration: DownloadServerConfiguration;
    identityVerificationConfiguration: IdentityVerificationConfiguration;
    sdkConfiguration: SdkConfiguration;
}

export interface ConfigurationDto {
    tenant_configuration: Record<string, unknown>;
    bbti_configuration: BBTIConfiguration;
    validation_configuration: ValidationConfiguration;
    insurance_configuration: InsuranceConfiguration;
    ntf_configuration: NtfConfiguration;
    lease_configuration: LeaseConfiguration;
    download_app_page_configuration: DownloadServerConfiguration;
    identity_verification_configuration: IdentityVerificationConfiguration;
    sdk_configuration: SdkConfiguration;
}

export interface EmsConfiguration {
    emsId: string;
    hlEntitlements: boolean;
    slEntitlements: boolean;
    availableSlActivations: number;
    usedSlActivations: number;
    lastSynced: string | null;
}

export interface EmsConfigurationDto {
    ems_id: string | null;
    hl_entitlements: boolean;
    sl_entitlements: boolean;
    available_sl_activations: number;
    used_sl_activations: number;
    last_synced: string | null;
}

export function toEmsConfiguration(dto: EmsConfigurationDto): EmsConfiguration {
    return {
        // Not ideal to use an empty string but React basically requires it.
        emsId: dto.ems_id ?? "",
        hlEntitlements: dto.hl_entitlements,
        slEntitlements: dto.sl_entitlements,
        availableSlActivations: dto.available_sl_activations,
        usedSlActivations: dto.used_sl_activations,
        lastSynced: dto.last_synced,
    };
}

export function toEmsConfigurationDto(configuration: EmsConfiguration): EmsConfigurationDto {
    // Since toEmsConfiguration changes null to empty string, the reverse is performed here.
    const identifier = configuration.emsId === "" ? null : configuration.emsId;
    return {
        ems_id: identifier,
        hl_entitlements: configuration.hlEntitlements,
        sl_entitlements: configuration.slEntitlements,
        available_sl_activations: configuration.availableSlActivations,
        used_sl_activations: configuration.usedSlActivations,
        last_synced: configuration.lastSynced,
    };
}
export const deliveryTypes = ["NEW_DEAL", "REIMBURSEMENT", "SUPPLEMENTARY", "CORRECTION", "CONVERSION"];
export const trialDeliveryType = ["TRIAL"];
export type DeliveryType = (typeof deliveryTypes)[number] | (typeof trialDeliveryType)[number];

export enum DeliveryStatus {
    ALL = "ALL",
    SCHEDULED = "SCHEDULED",
    DELIVERED = "DELIVERED",
    DELETED = "DELETED",
    ON_HOLD = "ON_HOLD",
    TRIGGERED_MANUALLY = "TRIGGERED_MANUALLY",
    FAILED = "FAILED",
}

export enum DeliveryMode {
    ADD = "ADD",
    SET = "SET",
}
export interface LicenseToDeliver {
    licenseId: string;
    amount: number;
    expirationDate: string;
    totalOfLicenses: number;
    deliveryMode?: string;
}

export interface LicensePoolLicense extends LicenseToDeliver {
    used: number;
    license?: string;
    product?: string;
}

export interface PoolSpecificLicenseResponse {
    all_licenses: LicensePoolLicense[];
}
export interface DeliveryHistory extends TableData {
    uuid: string;
    creationDate: string;
    notes: string;
    type: string;
    licenseRateVersion: string;
    deliverySchedule?: string;
    deliveryStatus?: string;
    editorUsername?: string;
    editDate?: string;
}

export interface DeliveryHistories {
    deliveryHistories: DeliveryHistory[];
    count: number;
    cursor: string;
}

export interface LicenseDelivery {
    type: DeliveryType;
    caseNumber: string;
    opportunityId: string;
    notes: string;
    licenses: LicenseToDeliver[];
    tenantUuid?: string;
    tokenRateVersion?: string;
    toPopulatePools?: boolean;
    targetDate?: string;
    status?: string;
    editorUuid?: string;
    editDate?: string;
    licenseDeliveryUuid?: string;
    deliveryMode?: DeliveryMode;
    creationDate?: string;
}

export interface LicenseDeliveryHistory {
    uuid: string;
    type: DeliveryType;
    caseNumber: string;
    opportunityId: string;
    notes: string;
    licenses: LicenseToDeliver[];
    creationDate: string;
    creatorUuid: string;
    editorUuid: string;
    editDate: string;
    licenseRateVersion?: string;
    status?: string;
    targetDate?: string;
}

export interface TokenRates {
    name: string;
    uuid: string;
    productToRate: string;
    creationDate: string;
}

export interface ProductMappingItem {
    productName: string;
    description: string;
    onboarding?: "BDE" | "BMDE" | "AUTOMATION" | "BMPOP" | "BAR" | "BMWE" | undefined;
    licenses?: ProductOnboardingItem[];
    onboardingDescription?: string;
    deviceType?: DeviceType;
}

export interface ProductOnboardingItem extends LicenseData {
    productName: string;
    description: string;
    onboarding: string;
    downloadUrl?: string;
    versions?: {
        version?: string;
        downloadUrl?: string;
    }[];
}

export enum DeviceType {
    LAPTOP = "LAPTOP",
    LOOSE_DRIVES = "LOOSE_DRIVES",
    SERVER = "SERVER",
    MOBILE = "MOBILE",
}

export const MIN_DATE = new Date("1970-01-01T00:00:00Z");
export const MAX_DATE = new Date("2090-12-31T23:59:59Z");
